import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, RouterProvider, useLocation, type RouteObject } from "react-router";
import "./styles.css";
import { ApiError } from "./api.ts";
import { BrandEffects, Shell } from "./components/Shell.tsx";
import { TabbedLayout } from "./components/ui.tsx";
import { ADMIN_GROUPS, LEGACY_ADMIN_PATHS } from "./lib/admin-nav.ts";
import { AdminMcpPage } from "./pages/AdminMcp.tsx";
import { BrandingPage } from "./pages/Branding.tsx";
import { LoginPage } from "./pages/Login.tsx";
import { HomePage } from "./pages/Home.tsx";
import { UsersPage } from "./pages/Users.tsx";
import { GroupsPage } from "./pages/Groups.tsx";
import { GroupAccessTab, GroupBudgetTab, GroupKnowledgeTab, GroupOverviewTab, GroupPage } from "./pages/GroupPage.tsx";
import { BriefingsPage } from "./pages/Briefings.tsx";
import { SsoPage } from "./pages/Sso.tsx";
import { AuditPage } from "./pages/Audit.tsx";
import { RuntimesPage } from "./pages/Runtimes.tsx";
import { ModelsPage } from "./pages/Models.tsx";
import { UsagePage } from "./pages/Usage.tsx";
import { SettingsPage } from "./pages/Settings.tsx";
import { GoalsPage } from "./pages/Goals.tsx";
import { AppsPage, BrowserTab, ChatAppsPage, ConnectionsIndex, ConnectionsLayout, DeveloperPage } from "./pages/Connections.tsx";
import { ChatPage } from "./pages/Chat.tsx";
import { ApprovalsPage } from "./pages/Approvals.tsx";
import { FilesPage } from "./pages/Files.tsx";
import { TasksPage } from "./pages/Tasks.tsx";
import { MessagingPage } from "./pages/Messaging.tsx";
import { LinkPage } from "./pages/Link.tsx";
import { SharedMemoryPage } from "./pages/SharedMemory.tsx";
import { SharingRulesPage } from "./pages/SharingRules.tsx";
import { MemoryTab, SharedByMePage } from "./pages/MyMemory.tsx";
import { MyBriefingsPage } from "./pages/MyBriefings.tsx";
import { SkillsAdminPage } from "./pages/Skills.tsx";
import { MySkillsPage } from "./pages/MySkills.tsx";
import { SyncPage } from "./pages/Sync.tsx";
import { AdminWebAppsPage, WebAppsPage } from "./pages/WebApps.tsx";
import { NeedsAssistant } from "./components/NeedsAssistant.tsx";
import { HoursSavedPage } from "./pages/HoursSaved.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: false },
  },
});

/** The page behind each admin tab, by "group/tab". */
const adminPages: Record<string, React.ReactNode> = {
  "people/users": <UsersPage />,
  "people/groups": <GroupsPage />,
  "people/sso": <SsoPage />,
  "people/audit": <AuditPage />,
  "knowledge/shared": <SharedMemoryPage />,
  "knowledge/rules": <SharingRulesPage />,
  "knowledge/skills": <SkillsAdminPage />,
  "knowledge/sync": <SyncPage />,
  "assistants/list": <RuntimesPage />,
  "assistants/models": <ModelsPage />,
  "assistants/briefings": <BriefingsPage />,
  "assistants/usage": <UsagePage />,
  "workspace/hours-saved": <HoursSavedPage />,
  "workspace/messaging": <MessagingPage />,
  "workspace/mcp": <AdminMcpPage />,
  "workspace/branding": <BrandingPage />,
  "workspace/web-apps": <AdminWebAppsPage />,
};

/** Old URLs keep working (bookmarks, OAuth return links, chat-app messages), with ?query and #hash. */
function Go({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

const adminRoutes: RouteObject = {
  path: "admin",
  children: [
    { index: true, element: <Navigate to={ADMIN_GROUPS[0]!.base} replace /> },
    ...ADMIN_GROUPS.map((g): RouteObject => {
      if (!g.tabs.length) return { path: g.base, element: adminPages[g.base] };
      return {
        path: g.base,
        element: <TabbedLayout tabs={g.tabs.map((t) => ({ to: t.path, label: t.label }))} />,
        children: [
          { index: true, element: <Navigate to={g.tabs[0]!.path} replace /> },
          ...g.tabs.map((t): RouteObject => ({ path: t.path, element: adminPages[`${g.base}/${t.path}`] })),
          // A group's own page, with its own tabs (the Groups tab stays highlighted).
          ...(g.base === "people"
            ? [
                {
                  path: "groups/:id",
                  element: <GroupPage />,
                  children: [
                    { index: true, element: <Navigate to="overview" replace /> },
                    { path: "overview", element: <GroupOverviewTab /> },
                    { path: "access", element: <GroupAccessTab /> },
                    { path: "knowledge", element: <GroupKnowledgeTab /> },
                    { path: "budget", element: <GroupBudgetTab /> },
                  ],
                },
              ]
            : []),
        ],
      };
    }),
    ...Object.entries(LEGACY_ADMIN_PATHS).map(([from, to]): RouteObject => ({ path: from, element: <Go to={`/admin/${to}`} /> })),
  ],
};

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <Shell />,
    children: [
      // Chat is the front door; the old home page is the dashboard.
      { index: true, element: <Navigate to="/chat" replace /> },
      { path: "dashboard", element: <HomePage /> },
      // One route (optional id) so the page stays mounted when a new chat gets its id mid-stream.
      { path: "chat/:sessionId?", element: <NeedsAssistant fullBleed><ChatPage /></NeedsAssistant> },
      { path: "approvals/:id?", element: <ApprovalsPage /> },
      { path: "files", element: <NeedsAssistant title="Files"><FilesPage /></NeedsAssistant> },
      {
        path: "schedules",
        element: <TabbedLayout tabs={[{ to: "/schedules/tasks", label: "Scheduled tasks" }, { to: "/schedules/briefings", label: "Briefings" }]} />,
        children: [
          { index: true, element: <Navigate to="tasks" replace /> },
          { path: "tasks", element: <NeedsAssistant title="Scheduled tasks"><TasksPage /></NeedsAssistant> },
          { path: "briefings", element: <MyBriefingsPage /> },
        ],
      },
      { path: "settings", element: <SettingsPage /> },
      { path: "goals", element: <GoalsPage /> },
      { path: "web-apps", element: <WebAppsPage /> },
      {
        path: "memory",
        element: <TabbedLayout tabs={[{ to: "/memory/mine", label: "My memory" }, { to: "/memory/shared", label: "Shared by me" }, { to: "/memory/team", label: "Team & company" }]} />,
        children: [
          { index: true, element: <Navigate to="mine" replace /> },
          { path: "mine", element: <MemoryTab scope="mine" /> },
          { path: "shared", element: <SharedByMePage /> },
          { path: "team", element: <MemoryTab scope="team" /> },
        ],
      },
      { path: "skills", element: <MySkillsPage /> },
      { path: "link/:code", element: <LinkPage /> },
      {
        path: "connections",
        element: <ConnectionsLayout />,
        children: [
          { index: true, element: <ConnectionsIndex /> },
          { path: "chat", element: <ChatAppsPage /> },
          { path: "apps", element: <AppsPage /> },
          { path: "browser", element: <BrowserTab /> },
          { path: "developer", element: <DeveloperPage /> },
        ],
      },
      // Where these pages used to be (the assistant's links, OAuth returns and bookmarks still use some).
      { path: "tasks", element: <Go to="/schedules/tasks" /> },
      { path: "browser", element: <Go to="/connections/browser" /> },
      { path: "secrets", element: <Go to="/connections/developer" /> },
      { path: "integrations/mcp", element: <Go to="/connections/developer" /> },
      { path: "mcp", element: <Go to="/connections/developer" /> },
      adminRoutes,
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrandEffects />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
